// Help (D22, UC-36): what the dashboard shows of the walkthroughs the server
// lists. The server decides which walkthroughs, in which language and in
// which order; this only turns its answer into what the screen draws: the
// section names, where each walkthrough happens, and how many a section holds.
// What the player does with one walkthrough's scenes is in player.ts.

import type { components } from '../api/types';
import type { MessageKey } from '../i18n/messages';

export type HelpScene = components['schemas']['HelpSceneBody'];
export type Walkthrough = Omit<components['schemas']['HelpWalkthroughBody'], 'scenes'> & {
	scenes: HelpScene[];
};
export type HelpSectionName = components['schemas']['HelpSectionBody']['section'];
export interface HelpSection {
	section: HelpSectionName;
	walkthroughs: Walkthrough[];
}

/** Each section's name in the dashboard's language. */
export const sectionNames: Record<HelpSectionName, MessageKey> = {
	'getting-started': 'helpGettingStarted',
	till: 'helpTill',
	menu: 'helpMenu',
	'day-to-day': 'helpDayToDay',
	account: 'helpAccount',
};

/** Where a walkthrough happens, in words. */
export const surfaceNames: Record<Walkthrough['surface'], MessageKey> = {
	till: 'helpOnTheTill',
	owner: 'helpOnTheDashboard',
};

/**
 * The answer as the screen reads it. A list the server may send as null is an
 * empty one. A section or a surface this bundle has no words for is left out,
 * with the walkthroughs in it, so a bundle still open when a later server
 * brings a new one shows what it can (INT-7).
 */
export function shelfOf(
	sections: components['schemas']['HelpSectionBody'][] | null | undefined,
): HelpSection[] {
	return (sections ?? [])
		.filter((s) => Object.hasOwn(sectionNames, s.section))
		.map((s) => ({
			section: s.section,
			walkthroughs: (s.walkthroughs ?? [])
				.filter((w) => Object.hasOwn(surfaceNames, w.surface))
				.map((w) => ({ ...w, scenes: w.scenes ?? [] })),
		}))
		.filter((s) => s.walkthroughs.length > 0);
}

/** The form a counted word takes for a number, as Arabic counts: 1, 2, 3 to 10, or more. */
export type CountForm = 'One' | 'Two' | 'Few' | 'Many';

export function countForm(n: number): CountForm {
	if (n === 1) return 'One';
	if (n === 2) return 'Two';
	return n >= 3 && n <= 10 ? 'Few' : 'Many';
}

/** How many walkthroughs a section holds, in the words for that number. */
export function videosKey(n: number): MessageKey {
	return `helpVideos${countForm(n)}`;
}

/** How many scenes a walkthrough has, in the words for that number. */
export function scenesKey(n: number): MessageKey {
	return `helpScenes${countForm(n)}`;
}
